import { useEffect, useState } from 'react';
import _ from 'lodash';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, DeleteIcon, DragHandleIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, FormControl, FormLabel, Heading } from '@chakra-ui/react';

import { Circuit, DayExercise, GetProgramResopnse } from '../../lib/types/programEdit';
import StrongInput from '../common/StrongInput';
import { ExercisePlan } from './ExercisePlan';
import { CustomTitle } from './CustomTitle';

type CircuitPlanProps = {
  weekIndex: number;
  dayIndex: number;
  circuitIndex: number;
  circuit: Circuit;
  program: GetProgramResopnse;
  updateProgram: (updatedProgram: GetProgramResopnse) => void;
  isWarmup?: boolean;
};

export const CircuitPlan = ({
  weekIndex,
  dayIndex,
  circuitIndex,
  circuit,
  program,
  updateProgram,
  isWarmup = false,
}: CircuitPlanProps) => {
  const [roundsInput, setRoundsInput] = useState(String(circuit.rounds ?? 1));
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setRoundsInput(String(circuit.rounds ?? 1));
  }, [circuit.rounds]);

  const addCircuitExercise = (
    nextWeekIndex: number,
    nextDayIndex: number,
    newTypeId: number,
    newFormats: string[],
  ) => {
    const updatedProgram = _.cloneDeep(program);
    const newExercise: DayExercise = {
      typeId: newTypeId,
      exerciseId: '',
      guide: '',
      sets: 0,
      reps: 0,
      weight: 0,
      rest: 0,
      per_side: false,
      custom_text: '',
      formats: newFormats,
      status: '',
      extra: [],
    };

    if (isWarmup) {
      if (!updatedProgram.weeks[nextWeekIndex].days[nextDayIndex].warmups) {
        updatedProgram.weeks[nextWeekIndex].days[nextDayIndex].warmups = [];
      }
      updatedProgram.weeks[nextWeekIndex].days[nextDayIndex].warmups![
        circuitIndex
      ].circuitExercises.push(newExercise);
    } else {
      updatedProgram.weeks[nextWeekIndex].days[nextDayIndex].circuits[
        circuitIndex
      ].circuitExercises.push(newExercise);
    }

    updateProgram(updatedProgram);
  };

  const reassignCircuitExerciseTypeIds = (deletedExerciseTypeId: number) => {
    const updatedProgram = _.cloneDeep(program);
    const exercises = isWarmup
      ? updatedProgram.weeks[weekIndex].days[dayIndex].warmups![circuitIndex]
          .circuitExercises
      : updatedProgram.weeks[weekIndex].days[dayIndex].circuits[circuitIndex]
          .circuitExercises;

    exercises.forEach((exercise) => {
      if (
        exercise.typeId !== undefined &&
        exercise.typeId > deletedExerciseTypeId
      ) {
        exercise.typeId--;
      }
    });

    updateProgram(updatedProgram);
  };

  const updateCircuit = (key: keyof Circuit, value: string | number) => {
    const updatedProgram = _.cloneDeep(program);
    if (isWarmup) {
      updatedProgram.weeks[weekIndex].days[dayIndex].warmups![circuitIndex] = {
        ...updatedProgram.weeks[weekIndex].days[dayIndex].warmups![circuitIndex],
        [key]: value,
      };
    } else {
      updatedProgram.weeks[weekIndex].days[dayIndex].circuits[circuitIndex] = {
        ...updatedProgram.weeks[weekIndex].days[dayIndex].circuits[circuitIndex],
        [key]: value,
      };
    }
    updateProgram(updatedProgram);
  };

  const deleteCircuit = () => {
    const updatedProgram = _.cloneDeep(program);
    if (isWarmup) {
      const warmups = updatedProgram.weeks[weekIndex].days[dayIndex].warmups || [];
      warmups.splice(circuitIndex, 1);
      warmups.forEach((item, index) => {
        item.typeId = index + 1;
        // Preserve user-set titles — only typeId needs reindexing.
      });
    } else {
      const circuits = updatedProgram.weeks[weekIndex].days[dayIndex].circuits;
      circuits.splice(circuitIndex, 1);
      circuits.forEach((item, index) => {
        item.typeId = index + 1;
        // Preserve user-set titles — only typeId needs reindexing.
      });
    }

    updateProgram(updatedProgram);
  };

  const moveCircuitExercise = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= circuit.circuitExercises.length) return;

    const updatedProgram = _.cloneDeep(program);
    const exercises = isWarmup
      ? updatedProgram.weeks[weekIndex].days[dayIndex].warmups![circuitIndex].circuitExercises
      : updatedProgram.weeks[weekIndex].days[dayIndex].circuits[circuitIndex].circuitExercises;

    const temp = exercises[index];
    exercises[index] = exercises[targetIndex];
    exercises[targetIndex] = temp;

    updateProgram(updatedProgram);
  };

  const moveCircuitExerciseToTarget = (fromIndex: number, toIndex: number) => {
    const updatedProgram = _.cloneDeep(program);
    const exercises = isWarmup
      ? updatedProgram.weeks[weekIndex].days[dayIndex].warmups![circuitIndex].circuitExercises
      : updatedProgram.weeks[weekIndex].days[dayIndex].circuits[circuitIndex].circuitExercises;

    const [moved] = exercises.splice(fromIndex, 1);
    exercises.splice(toIndex, 0, moved);

    updateProgram(updatedProgram);
  };

  const getNextExerciseTypeId = () => {
    const typeIds = circuit.circuitExercises
      .map((exercise) => exercise.typeId)
      .filter((typeId): typeId is number => typeof typeId === 'number');

    return Math.max(0, ...typeIds) + 1;
  };

  return (
    <Box p={4} bg='primary.50' borderRadius='md' boxShadow='sm' my={4}>
      <Flex justify='space-between' align='center' mb={4}>
        <CustomTitle
          type={isWarmup ? "Warmup" : "Circuit"}
          index={circuitIndex + 1}
          customTitle={circuit.title || (isWarmup ? `Warmup ${circuitIndex + 1}` : `Circuit ${circuitIndex + 1}`)}
          updateFunction={(newTitle) => updateCircuit('title', newTitle)}
        />
        <Flex gap={2}>
          <Button
            backgroundColor='primary.300'
            leftIcon={<DeleteIcon mr='-2' />}
            _hover={{ bg: 'primary.500' }}
            onClick={deleteCircuit}
          />
          <Button
            backgroundColor='primary.300'
            leftIcon={isCollapsed ? <ChevronDownIcon mr='-2' /> : <ChevronUpIcon mr='-2' />}
            _hover={{ bg: 'primary.500' }}
            onClick={() => setIsCollapsed((prev) => !prev)}
          />
        </Flex>
      </Flex>

      {!isCollapsed && (
        <>
          <FormControl maxW='220px' mt={4}>
            <FormLabel>Rounds</FormLabel>
            <StrongInput
              type='number'
              min={1}
              value={roundsInput}
              onChange={(e) => {
                const { value } = e.target;
                setRoundsInput(value);

                if (value === '') return;

                const nextRounds = Number(value);
                if (!Number.isNaN(nextRounds) && nextRounds >= 1) {
                  updateCircuit('rounds', nextRounds);
                }
              }}
              onBlur={() => {
                const nextRounds = Math.max(1, Number(roundsInput) || 1);
                setRoundsInput(String(nextRounds));
                updateCircuit('rounds', nextRounds);
              }}
            />
          </FormControl>

          {circuit.circuitExercises.map((exercise, exerciseIndex, arr) => {
            if (!exercise) return null;
            const isFirst = exerciseIndex === 0;
            const isLast = exerciseIndex === arr.length - 1;
            return (
              <Box
                key={exercise._id ?? `${isWarmup ? 'warmup' : 'circuit'}-exercise-${exercise.typeId ?? exerciseIndex}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", String(exerciseIndex));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  const dragIndex = Number(e.dataTransfer.getData("text/plain"));
                  if (!isNaN(dragIndex) && dragIndex !== exerciseIndex) {
                    moveCircuitExerciseToTarget(dragIndex, exerciseIndex);
                  }
                }}
                border="1px solid transparent"
                _hover={{ borderColor: "gray.200", borderRadius: "md" }}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  mt={2}
                  px={1}
                  cursor="grab"
                  _active={{ cursor: "grabbing" }}
                >
                  <Box fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                    Exercise {exerciseIndex + 1} Order
                  </Box>
                  <Flex gap={2}>
                    <Button
                      size="xs"
                      colorScheme="teal"
                      variant="solid"
                      isDisabled={isFirst}
                      onClick={() => moveCircuitExercise(exerciseIndex, 'up')}
                      leftIcon={<ChevronUpIcon />}
                    >
                      Move Up
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="teal"
                      variant="solid"
                      isDisabled={isLast}
                      onClick={() => moveCircuitExercise(exerciseIndex, 'down')}
                      leftIcon={<ChevronDownIcon />}
                    >
                      Move Down
                    </Button>
                    <Box display="flex" alignItems="center" pl={2} cursor="grab" _active={{ cursor: "grabbing" }}>
                      <DragHandleIcon color="gray.400" />
                    </Box>
                  </Flex>
                </Flex>
                <ExercisePlan
                  weekIndex={weekIndex}
                  dayIndex={dayIndex}
                  circuitIndex={circuitIndex}
                  exerciseIndex={exerciseIndex}
                  exercise={exercise}
                  addExercise={addCircuitExercise}
                  reassignExerciseTypeIds={reassignCircuitExerciseTypeIds}
                  program={program}
                  updateProgram={updateProgram}
                  addedExNum={-1}
                  isWarmup={isWarmup}
                />
              </Box>
            );
          })}

          <Button
            colorScheme='primary'
            variant='outline'
            leftIcon={<AddIcon />}
            mt={4}
            onClick={() =>
              addCircuitExercise(
                weekIndex,
                dayIndex,
                getNextExerciseTypeId(),
                [],
              )
            }
          >
            {isWarmup ? 'Add Exercise To Warmup' : 'Add Exercise To Circuit'}
          </Button>
        </>
      )}
    </Box>
  );
};
